/**
 * Componente para subir archivos con preview y validaciones
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 2.4
 */

import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

/**
 * Componente AdjuntoUpload
 * 
 * @param {Object} props
 * @param {Array} props.adjuntos - Array de archivos adjuntos (objetos con name, type, size, data)
 * @param {Function} props.onChange - Callback cuando cambian los adjuntos
 * @param {number} props.maxFiles - Máximo número de archivos (default: 5)
 * @param {number} props.maxSizeMB - Tamaño máximo por archivo en MB (default: 50)
 * @returns {JSX.Element}
 */
const AdjuntoUpload = ({ adjuntos = [], onChange, maxFiles = 5, maxSizeMB = 50 }) => {
  const fileInputRef = useRef(null);
  const [error, setError] = useState('');
  
  /**
   * Manejar selección de archivos
   */
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setError('');
    
    // Validar cantidad
    if (adjuntos.length + files.length > maxFiles) {
      setError(`Máximo ${maxFiles} archivos permitidos`);
      return;
    }
    
    // Procesar archivos
    const promises = files.map(file => {
      return new Promise((resolve, reject) => {
        // Validar tamaño
        const sizeMB = file.size / (1024 * 1024);
        if (sizeMB > maxSizeMB) {
          reject(`El archivo "${file.name}" excede el tamaño máximo de ${maxSizeMB}MB`);
          return;
        }
        
        // Leer como Base64
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            name: file.name,
            type: file.type,
            size: file.size,
            data: e.target.result // Base64 string (data:image/png;base64,...)
          });
        };
        reader.onerror = () => reject(`Error leyendo archivo "${file.name}"`);
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(promises)
      .then(newFiles => {
        onChange([...adjuntos, ...newFiles]);
      })
      .catch(err => {
        setError(err);
      });
    
    // Resetear input para permitir seleccionar el mismo archivo de nuevo
    event.target.value = '';
  };
  
  /**
   * Eliminar archivo de la lista
   */
  const handleRemove = (index) => {
    const newAdjuntos = adjuntos.filter((_, i) => i !== index);
    onChange(newAdjuntos);
  };
  
  /**
   * Formatear tamaño de archivo
   */
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  
  return (
    <Box>
      {/* Input file oculto */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        multiple
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
      />
      
      {/* Botón para seleccionar archivos */}
      <Button
        variant="outlined"
        startIcon={<AttachFileIcon />}
        onClick={() => fileInputRef.current?.click()}
        disabled={adjuntos.length >= maxFiles}
      >
        Seleccionar Archivos ({adjuntos.length}/{maxFiles})
      </Button>
      
      {/* Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Lista de archivos seleccionados */}
      {adjuntos.length > 0 && (
        <Box sx={{ mt: 1, display: 'block', width: 'fit-content' }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 1.5, 
            bgcolor: 'rgba(0, 0, 0, 0.02)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <List dense sx={{ py: 0 }}>
              {adjuntos.map((file, index) => (
                <ListItem
                  key={index}
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                    borderRadius: 1,
                    mb: 0.5,
                    px: 1.5,
                    pr: 6, // Espacio para el botón de eliminar
                    '&:last-child': { mb: 0 }
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleRemove(index)}
                      size="small"
                      aria-label="eliminar archivo"
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <InsertDriveFileIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', minWidth: '24px' }} />
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                    sx={{ pr: 4 }} // Espacio adicional para evitar sobreposición
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      )}
      
      {/* Información de límites */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Máximo {maxFiles} archivos. Tamaño máximo por archivo: {maxSizeMB}MB
      </Typography>
    </Box>
  );
};

export default AdjuntoUpload;

